import React, { useState, useMemo } from 'react';
import { buildCron, describeCron } from '../automationUtils';

const DAYS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
];

const REPEATS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

// Parse an existing 5-field cron back into builder state so editing a custom
// schedule reopens with the previous selection. Falls back to sensible
// defaults for anything it doesn't recognize.
const parseInitial = (cron) => {
  const fallback = { repeat: 'weekly', daysOfWeek: [1], dayOfMonth: 1, time: '09:00' };
  if (!cron || typeof cron !== 'string') return fallback;

  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return fallback;

  const [min, hr, dom, , dow] = parts;
  const hasTime = /^\d+$/.test(min) && /^\d+$/.test(hr);
  const time = hasTime
    ? `${String(parseInt(hr, 10)).padStart(2, '0')}:${String(parseInt(min, 10)).padStart(2, '0')}`
    : '09:00';

  if (/^\d+$/.test(dom)) {
    return { repeat: 'monthly', daysOfWeek: [1], dayOfMonth: Math.min(28, parseInt(dom, 10) || 1), time };
  }

  if (dow !== '*' && /^[0-9,]+$/.test(dow)) {
    const days = dow.split(',').map((d) => parseInt(d, 10)).filter((d) => !Number.isNaN(d));
    return { repeat: 'weekly', daysOfWeek: days.length ? days : [1], dayOfMonth: 1, time };
  }

  if (dom === '*' && dow === '*' && hasTime) {
    return { repeat: 'daily', daysOfWeek: [1], dayOfMonth: 1, time };
  }

  return { ...fallback, time };
};

/**
 * Guided custom schedule builder. Rendered inside the ScheduleModal popover
 * shell. Produces a cron expression and hands it to onSave via the parent.
 */
const CustomScheduleForm = ({ initialCron, onBack, onSave, saving }) => {
  const init = useMemo(() => parseInitial(initialCron), [initialCron]);
  const [repeat, setRepeat] = useState(init.repeat);
  const [daysOfWeek, setDaysOfWeek] = useState(init.daysOfWeek);
  const [dayOfMonth, setDayOfMonth] = useState(init.dayOfMonth);
  const [time, setTime] = useState(init.time);

  const cron = useMemo(
    () => buildCron({ repeat, daysOfWeek, dayOfMonth, time }),
    [repeat, daysOfWeek, dayOfMonth, time]
  );

  const weeklyHasNoDays = repeat === 'weekly' && daysOfWeek.length === 0;

  const toggleDay = (value) => {
    setDaysOfWeek((prev) =>
      prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value]
    );
  };

  const handleSave = () => {
    if (saving || weeklyHasNoDays) return;
    onSave(cron);
  };

  return (
    <div className="p-5 space-y-5">
      {/* Repeat */}
      <div>
        <label className="block text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
          Repeat
        </label>
        <div className="grid grid-cols-3 gap-1 bg-gray-100 dark:bg-white/[0.04] rounded-xl p-1">
          {REPEATS.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setRepeat(r.value)}
              className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-all duration-150
                ${repeat === r.value
                  ? 'bg-white dark:bg-white/[0.08] text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Weekly: day-of-week chips */}
      {repeat === 'weekly' && (
        <div>
          <label className="block text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
            On
          </label>
          <div className="flex flex-wrap gap-1.5">
            {DAYS.map((d) => {
              const selected = daysOfWeek.includes(d.value);
              return (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => toggleDay(d.value)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all duration-150
                    ${selected
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-sm'
                      : 'bg-gray-100 dark:bg-white/[0.04] text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/[0.08] hover:text-gray-900 dark:hover:text-white'
                    }`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
          {weeklyHasNoDays && (
            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
              Select at least one day.
            </p>
          )}
        </div>
      )}

      {/* Monthly: day-of-month */}
      {repeat === 'monthly' && (
        <div>
          <label className="block text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
            Day of month
          </label>
          <input
            type="number"
            min={1}
            max={28}
            value={dayOfMonth}
            onChange={(e) => setDayOfMonth(e.target.value)}
            className="w-full px-3 py-2 rounded-xl text-sm bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-transparent transition-all"
          />
          <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">
            1–28 (avoids months that skip days 29–31).
          </p>
        </div>
      )}

      {/* Time */}
      <div>
        <label className="block text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
          At
        </label>
        <input
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className="w-full px-3 py-2 rounded-xl text-sm bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-transparent transition-all"
        />
      </div>

      {/* Live preview */}
      <div className="px-3.5 py-2.5 rounded-xl bg-gray-50 dark:bg-white/[0.02] border border-gray-200/60 dark:border-white/[0.06] text-gray-700 dark:text-gray-300 text-xs">
        <span className="text-gray-400 dark:text-gray-500">Runs </span>
        {weeklyHasNoDays ? 'when a day is selected' : describeCron(cron).toLowerCase()}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={onBack}
          disabled={saving}
          className="flex-1 px-3 py-2 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-white/[0.04] hover:bg-gray-200 dark:hover:bg-white/[0.08] transition-all disabled:opacity-60"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || weeklyHasNoDays}
          className="flex-1 px-3 py-2 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {saving ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            'Save'
          )}
        </button>
      </div>
    </div>
  );
};

export default CustomScheduleForm;
